// src/utils/Forecasting.js
//
// Forecasting & Projection utilities for SiloGate Reporting & Analytics.
// All deterministic statistical models (no ML, no external dependencies) so
// results are fully explainable to auditors and end-users.
//
//   linearFit(points)            — ordinary-least-squares regression
//   projectKpiAnnual(kpi, fy)    — year-end forecast for an APP KPI
//   projectBudget(project, asOf) — final-spend forecast for a project
//   projectCompletion(project)   — forecast end-date from current progress
//   forecastConfidence(n)        — confidence band based on data-point count
//
// All functions return plain objects with the inputs they consumed, so the UI
// can show "how we calculated this" without re-running the maths.

// ────────────────────────────────────────────────────────────────────
// 1. Linear regression (ordinary least squares)
// ────────────────────────────────────────────────────────────────────
// points: Array<{ x:number, y:number }>
// returns: { slope, intercept, r2, predict(x) }
export function linearFit(points) {
  const n = points.length;
  if (n < 2) {
    const y = n === 1 ? points[0].y : 0;
    return { slope: 0, intercept: y, r2: 0, n, predict: () => y };
  }
  const sumX  = points.reduce((s, p) => s + p.x, 0);
  const sumY  = points.reduce((s, p) => s + p.y, 0);
  const sumXY = points.reduce((s, p) => s + p.x * p.y, 0);
  const sumXX = points.reduce((s, p) => s + p.x * p.x, 0);
  const sumYY = points.reduce((s, p) => s + p.y * p.y, 0);

  const denom = n * sumXX - sumX * sumX;
  const slope = denom === 0 ? 0 : (n * sumXY - sumX * sumY) / denom;
  const intercept = (sumY - slope * sumX) / n;

  // Pearson correlation coefficient → R²
  const denomR = Math.sqrt((n * sumXX - sumX * sumX) * (n * sumYY - sumY * sumY));
  const r = denomR === 0 ? 0 : (n * sumXY - sumX * sumY) / denomR;
  const r2 = r * r;

  return { slope, intercept, r2, n, predict: (x) => slope * x + intercept };
}

// ────────────────────────────────────────────────────────────────────
// 2. Forecast confidence band (qualitative)
// ────────────────────────────────────────────────────────────────────
// Used to label projections "Low / Moderate / High" based on how many data
// points fed the regression — purely a UX cue, not a statistical CI.
export function forecastConfidence(n, r2 = null) {
  if (n < 2) return { label: 'Insufficient', tone: 'slate' };
  if (n === 2) return { label: 'Low', tone: 'amber' };
  if (r2 != null && r2 < 0.3) return { label: 'Low', tone: 'amber' };
  if (n === 3) return { label: 'Moderate', tone: 'blue' };
  if (r2 != null && r2 >= 0.8) return { label: 'High', tone: 'emerald' };
  return { label: 'Moderate', tone: 'blue' };
}

// ────────────────────────────────────────────────────────────────────
// 3. KPI year-end projection
// ────────────────────────────────────────────────────────────────────
// For each APP KPI, fit a line through the quarters that have actuals
// recorded (Q1/Q2/Q3) and use it to predict any missing quarters. Annual
// projection = sum of (actual where present) + (predicted where absent).
//
// kpi: { annualTarget, q1Target..q4Target, actuals: [{quarter, actualValue, financialYear}] }
// fy:  financial year to project for
export function projectKpiAnnual(kpi, fy) {
  const QMAP = { Q1: 1, Q2: 2, Q3: 3, Q4: 4 };
  const fyActuals = (kpi.actuals || []).filter(a => a.financialYear === fy);
  const knownPoints = fyActuals
    .filter(a => a.actualValue != null)
    .map(a => ({ x: QMAP[a.quarter], y: a.actualValue }));

  const fit = linearFit(knownPoints);
  const annualTarget = kpi.annualTarget ?? 0;

  // Predict the value for each quarter (use actual if available, else fit)
  const quarterValues = [1, 2, 3, 4].map(q => {
    const known = fyActuals.find(a => QMAP[a.quarter] === q && a.actualValue != null);
    if (known) return { q, value: known.actualValue, isActual: true };
    if (knownPoints.length < 1) return { q, value: null, isActual: false };
    return { q, value: Math.max(0, fit.predict(q)), isActual: false };
  });

  const projectedAnnual = quarterValues.every(v => v.value != null)
    ? quarterValues.reduce((s, v) => s + v.value, 0)
    : null;

  const variance = projectedAnnual != null && annualTarget > 0
    ? ((projectedAnnual - annualTarget) / annualTarget) * 100
    : null;

  const status = variance == null
    ? 'No data'
    : variance >= 0 ? 'On Track'
    : variance >= -15 ? 'At Risk'
    : 'Likely Miss';

  return {
    kpiId: kpi.id,
    reference: kpi.reference,
    name: kpi.name,
    annualTarget,
    knownActuals: knownPoints.length,
    quarterValues,
    projectedAnnual,
    variance,
    status,
    fit,
    confidence: forecastConfidence(knownPoints.length, fit.r2)
  };
}

// ────────────────────────────────────────────────────────────────────
// 4. Budget burn-rate forecast
// ────────────────────────────────────────────────────────────────────
// Assumes linear spend over project duration. Computes monthly burn rate
// from spend-to-date / months-elapsed, then projects final spend over the
// full project duration.
//
// project: { startDate, endDate, totalBudget, actualSpend }
// asOf:    optional Date — defaults to today
export function projectBudget(project, asOf = new Date()) {
  const start = project.startDate ? new Date(project.startDate) : null;
  const end   = project.endDate   ? new Date(project.endDate)   : null;
  const budget = project.totalBudget || 0;
  const spent = project.actualSpend || 0;

  if (!start || !end || budget <= 0) {
    return {
      reference: project.reference, title: project.title,
      budget, spent, monthsElapsed: null, monthsTotal: null,
      monthlyBurn: null, forecastFinalSpend: null, variance: null,
      status: 'Insufficient data'
    };
  }

  const monthsTotal = Math.max(1, monthsBetween(start, end));
  const monthsElapsed = Math.max(0, Math.min(monthsTotal, monthsBetween(start, asOf)));
  const monthlyBurn = monthsElapsed > 0 ? spent / monthsElapsed : 0;
  const forecastFinalSpend = monthlyBurn * monthsTotal;
  const variance = budget > 0 ? ((forecastFinalSpend - budget) / budget) * 100 : null;

  const status = variance == null
    ? 'No data'
    : variance <= 0 ? 'On Budget'
    : variance <= 10 ? 'At Risk'
    : 'Over Budget';

  return {
    reference: project.reference, title: project.title,
    budget, spent,
    monthsElapsed: Math.round(monthsElapsed * 10) / 10,
    monthsTotal:   Math.round(monthsTotal   * 10) / 10,
    monthlyBurn,
    forecastFinalSpend,
    variance,
    status,
    confidence: forecastConfidence(Math.max(2, Math.round(monthsElapsed)))
  };
}

// ────────────────────────────────────────────────────────────────────
// 5. Project completion-date forecast
// ────────────────────────────────────────────────────────────────────
// Velocity = progress% / months-elapsed. Forecast end = start + (100 / velocity)
// months. Slip = forecast end − planned end.
//
// project: { startDate, endDate, progressPercentage }
export function projectCompletion(project, asOf = new Date()) {
  const start = project.startDate ? new Date(project.startDate) : null;
  const end   = project.endDate   ? new Date(project.endDate)   : null;
  const progress = project.progressPercentage || 0;

  if (!start || !end) {
    return {
      reference: project.reference, title: project.title,
      progress, plannedEnd: end, forecastEnd: null,
      slipDays: null, velocity: null, status: 'Insufficient data'
    };
  }

  const monthsElapsed = monthsBetween(start, asOf);
  if (monthsElapsed <= 0) {
    return {
      reference: project.reference, title: project.title,
      progress, plannedEnd: end, forecastEnd: end,
      slipDays: 0, velocity: 0, status: 'Not started'
    };
  }

  if (progress <= 0) {
    return {
      reference: project.reference, title: project.title,
      progress, plannedEnd: end, forecastEnd: null,
      slipDays: null, velocity: 0,
      status: 'Stalled — no progress recorded'
    };
  }

  if (progress >= 100) {
    return {
      reference: project.reference, title: project.title,
      progress: 100, plannedEnd: end, forecastEnd: asOf,
      slipDays: Math.round((asOf - end) / (1000 * 60 * 60 * 24)),
      velocity: progress / monthsElapsed,
      status: 'Completed'
    };
  }

  const velocity = progress / monthsElapsed; // % per month
  const monthsToComplete = (100 - progress) / velocity;
  const forecastEnd = addMonths(asOf, monthsToComplete);
  const slipDays = Math.round((forecastEnd - end) / (1000 * 60 * 60 * 24));

  const status = slipDays <= 0 ? 'On Schedule'
    : slipDays <= 30 ? 'Minor Slip'
    : slipDays <= 90 ? 'At Risk'
    : 'Significant Slip';

  return {
    reference: project.reference, title: project.title,
    progress, plannedEnd: end, forecastEnd, slipDays,
    velocity: Math.round(velocity * 100) / 100, status,
    confidence: forecastConfidence(Math.max(2, Math.round(monthsElapsed)))
  };
}

// ────────────────────────────────────────────────────────────────────
// 6. Risk trend direction
// ────────────────────────────────────────────────────────────────────
// Fits a line through (review-date, residual-score) for a single risk.
// Returns slope sign as Improving / Stable / Worsening.
//
// risk: { residualLikelihood, residualImpact, reviews?: [{reviewDate, residualScore}] }
export function riskTrendDirection(risk) {
  const reviews = (risk.reviews || risk.riskReviews || [])
    .filter(r => r.reviewDate && r.residualScore != null)
    .map(r => ({ x: new Date(r.reviewDate).getTime(), y: r.residualScore }))
    .sort((a, b) => a.x - b.x);

  if (reviews.length < 2) {
    return {
      reference: risk.reference, title: risk.title,
      currentScore: (risk.residualLikelihood || 0) * (risk.residualImpact || 0),
      direction: 'Stable', slope: 0, dataPoints: reviews.length
    };
  }

  // Normalise x to days from first review so the slope is interpretable
  const x0 = reviews[0].x;
  const normalised = reviews.map(p => ({ x: (p.x - x0) / (1000 * 60 * 60 * 24), y: p.y }));
  const fit = linearFit(normalised);

  // Significance threshold: less than 0.005 points/day = Stable
  const direction = fit.slope > 0.005 ? 'Worsening'
    : fit.slope < -0.005 ? 'Improving'
    : 'Stable';

  return {
    reference: risk.reference, title: risk.title,
    currentScore: (risk.residualLikelihood || 0) * (risk.residualImpact || 0),
    direction, slope: fit.slope, r2: fit.r2,
    dataPoints: reviews.length
  };
}

// ────────────────────────────────────────────────────────────────────
// Helpers
// ────────────────────────────────────────────────────────────────────
function monthsBetween(a, b) {
  const ms = b - a;
  return ms / (1000 * 60 * 60 * 24 * 30.4375); // mean month length
}
function addMonths(date, months) {
  const d = new Date(date.getTime());
  const wholeMonths = Math.floor(months);
  const fractionalDays = (months - wholeMonths) * 30.4375;
  d.setMonth(d.getMonth() + wholeMonths);
  d.setDate(d.getDate() + Math.round(fractionalDays));
  return d;
}

export default {
  linearFit, forecastConfidence, projectKpiAnnual,
  projectBudget, projectCompletion, riskTrendDirection
};
